/**
 * LoginForm Component
 *
 * Authentication form for user login with email and password.
 * Follows the same design patterns as RegistrationForm for consistency.
 *
 * @module components/auth/LoginForm
 */

import React, { useState, useCallback } from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/hooks/useTheme';
import { getAuthService } from '@/services/auth/AuthServiceFactory';
import type { LoginFormData } from '@/types/auth';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * LoginForm component props interface
 */
interface LoginFormProps {
  /** Success callback after successful login */
  onSuccess?: () => void;
  /** Callback to switch to sign up mode */
  onSwitchToSignUp?: () => void;
  /** Callback to switch to forgot password mode */
  onSwitchToForgotPassword?: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * LoginForm component for user authentication
 *
 * @example
 * ```tsx
 * <LoginForm
 *   onSuccess={handleLoginSuccess}
 *   onSwitchToSignUp={handleSwitchToSignUp}
 * />
 * ```
 */
export function LoginForm({ onSuccess, onSwitchToSignUp, onSwitchToForgotPassword, className = '' }: LoginFormProps) {
  const logger = getLogger();
  const { signIn, isLoading, error, clearError } = useAuth();
  const { isDark } = useTheme();
  const authService = getAuthService();

  // Form state
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

  /**
   * Handle field value changes
   */
  const handleFieldChange = useCallback((field: keyof LoginFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear global error
    if (error) {
      clearError();
    }
  }, [fieldErrors, error, clearError]);


  /**
   * Validate all form fields
   */
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!authService.validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    setFieldErrors(errors);

    // Mark all fields as touched to show errors
    setTouchedFields({
      email: true,
      password: true
    });

    return Object.keys(errors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const response = await signIn(formData);

      if (response.status === 'OK') {
        onSuccess?.();
      } else if (response.status === 'FIELD_ERROR' && response.formFields) {
        // Map field errors from SuperTokens response
        const errors: Record<string, string> = {};
        response.formFields.forEach(field => {
          errors[field.id] = field.error;
        });
        setFieldErrors(errors);
        setTouchedFields({ email: true, password: true });
      }
    } catch (error) {
      logger.error('Login form error:', error);
    }
  }, [isLoading, validateForm, signIn, formData, onSuccess]);

  /**
   * Get field validation props
   */
  const getFieldValidation = useCallback((fieldName: keyof LoginFormData) => {
    const shouldShowError = touchedFields[fieldName] && fieldErrors[fieldName];

    return {
      error: shouldShowError ? fieldErrors[fieldName] : undefined
    };
  }, [touchedFields, fieldErrors]);

  return (
    <div className={cn('w-full', className)}>
      {/* Form Header */}
      <div className="text-left mb-8">
        <h1 className={cn(
          "text-2xl font-bold mb-2",
          isDark ? "text-secondary-100" : "text-secondary-900"
        )}>
          Welcome Back
        </h1>
        <p className={cn(
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Login to your BiteWise provider account
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate data-testid="login-form">
        {/* Email Field */}
        <FormField
          id="email"
          label="Email Address"
          required
          {...getFieldValidation('email')}
        >
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            placeholder="Enter your email address"
            autoComplete="email"
            disabled={isLoading}
            required
            fullWidth
            data-testid="email-input"
          />
        </FormField>

        {/* Password Field */}
        <FormField
          id="password"
          label="Password"
          required
          {...getFieldValidation('password')}
        >
          <Input
            type="password"
            value={formData.password}
            onChange={(e) => handleFieldChange('password', e.target.value)}
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={isLoading}
            required
            fullWidth
            data-testid="password-input"
          />
        </FormField>

        {/* Forgot Password Link */}
        {onSwitchToForgotPassword && (
          <div className="text-right">
            <button
              type="button"
              onClick={onSwitchToForgotPassword}
              disabled={isLoading}
              className={cn(
                "text-sm font-medium hover:underline",
                isDark ? "text-primary-400" : "text-primary-600",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            >
              Forgot your password?
            </button>
          </div>
        )}

        {/* Global Error */}
        {error && (
          <div className={cn(
            "p-4 border rounded-lg",
            isDark ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
          )}>
            <div className="flex items-start">
              <svg className={cn(
                "w-5 h-5 mt-0.5 mr-3 flex-shrink-0",
                isDark ? "text-red-400" : "text-red-600"
              )} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className={cn(
                  "text-sm font-medium",
                  isDark ? "text-red-200" : "text-red-800"
                )}>
                  Login Failed
                </h3>
                <p className={cn(
                  "text-sm mt-1",
                  isDark ? "text-red-300" : "text-red-700"
                )}>
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={isLoading}
          disabled={isLoading}
          className="w-full"
          data-testid="login-button"
        >
          {isLoading ? 'Logging In...' : 'Login'}
        </Button>

        {/* Switch to Sign Up */}
        {onSwitchToSignUp && (
          <div className="text-center">
            <p className={cn(
              "text-sm",
              isDark ? "text-secondary-400" : "text-secondary-600"
            )}>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToSignUp}
                disabled={isLoading}
                className={cn(
                  "font-medium hover:underline",
                  isDark ? "text-primary-400" : "text-primary-600",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                Sign up
              </button>
            </p>
          </div>
        )}

        {/* Terms and Privacy */}
        <p className={cn(
          "text-xs text-center",
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          By signing in, you agree to our{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Terms of Service (opens in new window)"
            className={cn(
              "hover:underline inline-flex items-center gap-1",
              isDark ? "text-primary-400" : "text-primary-600"
            )}
          >
            Terms of Service
            <svg
              className="w-3 h-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>{' '}
          and{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Privacy Policy (opens in new window)"
            className={cn(
              "hover:underline inline-flex items-center gap-1",
              isDark ? "text-primary-400" : "text-primary-600"
            )}
          >
            Privacy Policy
            <svg
              className="w-3 h-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        </p>
      </form>
    </div>
  );
}